import { createGlobalStyle } from 'styled-components';
import { BRAND } from '@/constants/brand';
import { appShellGlobalStyles } from './appShellGlobalStyles';
import { APP_SHELL_THEME, shellThemeCss } from './appShellTheme';

const GlobalStyle = createGlobalStyle`
  ${shellThemeCss}

  html, body {
    background-color: ${APP_SHELL_THEME.pageBg};
  }

  body {
    margin: 0;
    padding: 0;
    font-family: ${BRAND.fontFamily};
    font-weight: ${BRAND.fontWeightLight};
    color: ${({ theme }) => theme.color.ink};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    min-height: 100vh;
    background-color: ${APP_SHELL_THEME.pageBg};
  }

  h1, h2, h3, h4, h5, h6,
  .ant-typography {
    font-family: ${BRAND.fontFamily};
  }

  .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled), 
  .ant-dropdown-menu-submenu .ant-dropdown-menu .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled) {
    color:${({ theme }) => theme.color.danger};
  }

  .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover, 
  .ant-dropdown-menu-submenu .ant-dropdown-menu .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
    background-color:${({ theme }) => theme.color.danger};
  }

  .ant-btn {
    box-shadow: none;
    
    &.ant-btn-lg {
      border-radius: 6px;
    }

    &.ant-btn-default,
    &.ant-btn-dashed {
      background: ${({ theme }) => theme.color.primary_1};
      border-color: ${({ theme }) => theme.color.primary_2};
      color: ${({ theme }) => theme.color.primary_6};

      &:hover {
        background: ${({ theme }) => theme.color.primary_2};
        border-color: ${({ theme }) => theme.color.primary_3};
        color: ${({ theme }) => theme.color.primary_6};
      }

      &:active {
        background: ${({ theme }) => theme.color.primary_6};
        border-color: ${({ theme }) => theme.color.primary_6};
        color: ${({ theme }) => theme.color.white};
      }

      &[disabled] {
        border-color: ${({ theme }) => theme.color.neutral_5};
        background-color: ${({ theme }) => theme.color.neutral_3};
        color: ${({ theme }) => theme.color.neutral_5};
      }
    }

    &.ant-btn-primary {
      font-weight: 500;
      background: ${({ theme }) => theme.color.primary_6};
      border-color: ${({ theme }) => theme.color.primary_6};

      &:hover {
        background: ${({ theme }) => theme.color.primary_5};
        border-color: ${({ theme }) => theme.color.primary_5};
        color: ${({ theme }) => theme.color.white};
      }

      &:active {
        background: ${({ theme }) => theme.color.primary_7};
        border-color: ${({ theme }) => theme.color.primary_7};
        color: ${({ theme }) => theme.color.white};
      }

      &[disabled] {
        border-color: ${({ theme }) => theme.color.neutral_5};
        background-color: ${({ theme }) => theme.color.neutral_3};
        color: ${({ theme }) => theme.color.neutral_5};
      }
    }

    &.ant-btn-link:not(.ant-btn-dangerous) {
      color: ${({ theme }) => theme.color.primary_6};

      &:hover,
      &:active {
        background: none;
        color: ${({ theme }) => theme.color.primary_6};
      }

      &[disabled] {
        color: ${({ theme }) => theme.color.black_50};
      }
    }

    &.ant-btn-link.ant-btn-dangerous {
      color: ${({ theme }) => theme.color.danger};

      &:hover,
      &:active {
        background: none;
        color: ${({ theme }) => theme.color.dust_red_4};
      }
    }

    &.ant-btn-text:not(.ant-btn-dangerous) {
      color: ${({ theme }) => theme.color.neutral_10};

      &:hover {
        background: ${({ theme }) => theme.color.neutral_2};
      }

      &:active {
        background: ${({ theme }) => theme.color.neutral_3};
      }
    }

    &.ant-btn-text.ant-btn-dangerous {
      color: ${({ theme }) => theme.color.danger};

      &:hover,
      &:active {
        background: none;
        color: ${({ theme }) => theme.color.dust_red_4};
      }
    }
  }

  .ant-table-wrapper {
    .ant-table-filter-trigger.active,
    .ant-table-column-sorter-up.active,
    .ant-table-column-sorter-down.active {
      color: ${({ theme }) => theme.color.primary_6};
    }

    .ant-table-tbody>tr>td {
      padding: 12px 16px;
    }
  }

  .ant-table-cell {
    .status {
      &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 100%;
      }

      &.draft::before {
        background-color: ${({ theme }) => theme.color.warning};
      }

      &.ready::before {
        background-color: ${({ theme }) => theme.color.success};
      }

      &.sent::before {
        background-color: ${({ theme }) => theme.color.primary_4};
      }
    }
  }

  .ant-pagination {
    & .ant-pagination-item {
      border-color: ${({ theme }) => theme.color.primary_2};
      background-color: ${({ theme }) => theme.color.white};

      a {
        color: ${({ theme }) => theme.color.primary_6};
      }
    }
    & .ant-pagination-item-active,
    & .ant-pagination-item-active:hover {
      border-color: ${({ theme }) => theme.color.primary_6};
      background-color: ${({ theme }) => theme.color.primary_1};

      a {
        color: ${({ theme }) => theme.color.primary_6};
      }
    }

    & .ant-pagination-item-link:not([disabled]) {
      border-color: ${({ theme }) => theme.color.primary_2};
    }

    & .ant-pagination-disabled:hover,
    & .ant-pagination-prev .ant-pagination-item-link,
    & .ant-pagination-next .ant-pagination-item-link {
      background-color: ${({ theme }) => theme.color.white};
    }

    .ant-select:not(.ant-select-customize-input) .ant-select-selector,
    .ant-pagination-options-quick-jumper input {
      border-color: ${({ theme }) => theme.color.primary_2};
    }

    .ant-pagination-options-quick-jumper input {
      color: ${({ theme }) => theme.color.white};
    }
  }

  .ant-checkbox .ant-checkbox-inner {
    border: ${({ theme }) => `1px solid ${theme.color.primary_2}`}
  }

  .ant-checkbox-disabled .ant-checkbox-inner:after {
    border-color: white;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    border-color:  ${({ theme }) => theme.color.primary_6};
    background-color: ${({ theme }) => theme.color.primary_6};
  }

  .ant-checkbox-wrapper:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
  .ant-checkbox:not(.ant-checkbox-disabled):hover .ant-checkbox-inner {
    border-color: ${({ theme }) => theme.color.primary_6};
  }

  .ant-checkbox-wrapper-checked:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-inner, .ant-checkbox-checked:not(.ant-checkbox-disabled):hover .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.color.primary_6};
    border-color: ${({ theme }) => theme.color.primary_6};
  }


  .ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover {
    & .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner {
      background-color: ${({ theme }) => theme.color.primary_6};
      border-color: ${({ theme }) => theme.color.primary_6};
    }

    & .ant-checkbox-checked:not(.ant-checkbox-disabled):after {
      border-color: ${({ theme }) => theme.color.primary_6};
    }
  }

  .ant-modal-confirm {
    .ant-modal-confirm-btns {
      margin-top: 32px;
    }

    .ant-modal-confirm-content {
      font-family: 'Inter'
    }

    .ant-modal-confirm-body >.anticon {
      font-size: 28px;
    }
  }

  .ant-input, .ant-select:not(.ant-select-customize-input) .ant-select-selector, .ant-picker {
    border-color: ${({ theme }) => theme.color.primary_2};
  }

  .ant-input-search {
    .ant-btn {
      background-color: ${({ theme }) => theme.color.neutral_1};
      border-color: ${({ theme }) => theme.color.primary_2};
    }
    .ant-btn.ant-btn-link:not(.ant-btn-dangerous):hover,
    .ant-btn.ant-btn-link:not(.ant-btn-dangerous):active {
      background-color: ${({ theme }) => theme.color.neutral_1};
    }
  }

  .ant-select-multiple.ant-select-lg .ant-select-selection-item {
    background-color: ${({ theme }) => theme.color.primary_2};
    color: ${({ theme }) => theme.color.primary_6};
    border: none;
  }

  .ant-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: ${({ theme }) => theme.color.primary_2};
  }

  .optional .ant-form-item-label::after{
    padding-left: 4px;
    color: ${({ theme }) => theme.color.secondary_45};
    content: '(optional)';
  }

  ${appShellGlobalStyles}
`;

export default GlobalStyle;
